<svelte:options
        customElement={{
    tag: "ofc-follow-brand-button-v1",
    props: {
      dreson: { type: "String" },
    },
  }}
/>

<script lang="ts">
    import {type BrandApiResponse, type Props} from "./follow-brand-button.types";
    import TrackingService from "./trackingService";
    import {consentChanged} from '@otto-ec/eprivacy-cmp'
    import {onCustomerSessionRefresh} from '@otto-ec/fti-customer-session-frontend'
    import {onexVariant} from "@otto-ec/global-resources/onex";

    const SNACKBAR_ID = "follow-brand-button-snackbar";
    const ERROR_SNACKBAR_ID = "follow-brand-button-error-snackbar";

    const trackingService = new TrackingService();

    let {dreson}: Props = $props();
    let isFollowing = $state(false)
    let isLoading = $state(false)
    let showButton = $state(false)
    let isInitializing = true

    const useNewCookieBannerLogic = onexVariant("E3919", "TestGroup");

    async function handleBrandButtonClick() {
        if (useNewCookieBannerLogic) {
            const apiResponse = await fetchIntentFollowState();
            const {status} = apiResponse;
            if (status === "NO_CONSENT") {
                await showBannerAndFollowAfterConsentGiven();
            } else {
                updateFollowStateViaApi(!isFollowing);
            }
        } else {
            updateFollowStateViaApi(!isFollowing);
        }
    }

    function updateFollowStateViaApi(follow: boolean) {
        const payload = {
            explicitContext: "BRAND",
            payload: dreson,
            action: follow ? "ADD" : "REMOVE",
        };

        isLoading = true;
        fetch('/promo-explicit-api/api/explicit-raw', {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        })
            .then((response) => {
                if (response.ok) {
                    trackingService.emitClickTrackingPayload(follow, dreson)
                    isFollowing = follow;
                    openSnackbar()
                } else {
                    openErrorSnackbar();
                }
            })
            .catch(_ => {
                openErrorSnackbar();
            })
            .finally(() => isLoading = false);
    }

    async function fetchIntentFollowState(): Promise<BrandApiResponse> {
        const rawResponse = await fetch(`/promo-explicit-api/api/brands/${dreson}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return rawResponse.json() as Promise<BrandApiResponse>;
    }

    function updateComponentState(status: BrandApiResponse['status']) {
        isFollowing = status === "FOLLOWING";

        if (useNewCookieBannerLogic) {
            /*                                                                                    */
            showButton = status !== "NO_CUSTOMER";
        } else {
            /*                                                                           */
            /*                                                           */
            showButton = status === "FOLLOWING" || status === "NOT_FOLLOWING";
        }
    }

    function handleTracking(totalFollowedBrands: number) {
        if (!showButton) {
            return
        }

        if (isInitializing) {
            trackingService.emitLoadedTracking(isFollowing, dreson, totalFollowedBrands)
        } else {
            trackingService.emitLoadedEventTracking(isFollowing, dreson, totalFollowedBrands)
        }
    }

    async function refreshState() {
        const apiResponse = await fetchIntentFollowState();
        const {status, totalFollowedBrands} = apiResponse
        updateComponentState(status);
        handleTracking(totalFollowedBrands);
    }

    function initializeBrandButton() {
        createSnackbar();
        createErrorSnackbar();
        refreshState().finally(() => {
            isInitializing = false
        });
    }

    function createSnackbar() {
        const snackbarElement = document.createElement("oc-snackbar-v1");
        snackbarElement.message = "";
        snackbarElement.open = false;
        snackbarElement.id = SNACKBAR_ID;
        snackbarElement.autoDismiss = true;

        const revokeAction = document.createElement('oc-link-v2');
        revokeAction.setAttribute('variant', 'inverted');
        revokeAction.setAttribute('slot', 'actions');
        revokeAction.setAttribute('as-button', 'true');
        revokeAction.setAttribute('oc-aria-label', 'Rückgängig');
        revokeAction.textContent = 'Rückgängig';
        revokeAction.onclick = () => handleBrandButtonClick();

        snackbarElement.append(revokeAction);
        document.querySelector("ofc-follow-brand-button-v1")!.after(snackbarElement);
    }

    function createErrorSnackbar() {
        const snackbarElement = document.createElement("oc-snackbar-v1");
        snackbarElement.message = "Fehler! Bitte probiere es noch mal.";
        snackbarElement.open = false;
        snackbarElement.id = ERROR_SNACKBAR_ID;
        document.querySelector("ofc-follow-brand-button-v1")!.after(snackbarElement);
    }

    function openSnackbar() {
        const snackbarElement = document.getElementById(SNACKBAR_ID) as any;
        snackbarElement.setAttribute("message", isFollowing ? "Gefolgt! Wir zeigen dir mehr Artikel deiner Lieblingsmarke." : "Von deinen Lieblingsmarken entfernt.");
        snackbarElement!.open = true;
    }

    function openErrorSnackbar() {
        const snackbarElement = document.getElementById(ERROR_SNACKBAR_ID) as any;
        snackbarElement.open = true;
    }

    initializeBrandButton();

    consentChanged.on(() => {
        refreshState()
    })

    onCustomerSessionRefresh.on(() => {
        refreshState()
    })

    function showBannerAndFollowAfterConsentGiven() {
        return new Promise((resolve) => {
            consentChanged.on(async () => {
                const apiResponse = await fetchIntentFollowState();
                if (apiResponse.status === 'NOT_FOLLOWING') {
                    updateFollowStateViaApi(true);
                }

                resolve(undefined);
            }, { once: true })

            window.otto.eprivacy.cff.openConsentBannerForFeature.emit({
                cookieBannerContext: 'TopBrandPromo'
            });
        })
    }

</script>
{#if showButton}
    <oc-button-v1 id="follow"
                  size="50"
                  variant="secondary"
                  oc-aria-label={isFollowing ? "Marke nicht mehr folgen" : "Marke folgen"}
                  onclick={handleBrandButtonClick}
                  icon-type-left={isFollowing ? "check": "plus"}
                  success={isFollowing}
                  loading={isLoading}
                  type="button">
        {isFollowing ? "Folge ich" : "Folgen"}
    </oc-button-v1>
{/if}